import { useEffect, useRef, type RefObject } from 'react';
import type { LayerSpecification, MapMouseEvent } from 'mapbox-gl';
import { UMR, type UMMapHandle } from '../../../urbanmap/_components';
import type { useNetwork } from './useNetwork';
import { pipeDensityColor } from './density';
import { PIPE_COLOR } from './networkDisplay';
import type { Coordinate } from './network';

type Controller = ReturnType<typeof useNetwork>;

/** Local adapter: public getMap API, no DOM lookup or shared component patch. */
export function useNetworkMap(ref: RefObject<UMMapHandle>, controller: Controller,
  options: { blocked: boolean; onDragStart: () => void; onPipeClick?: (id: string, at: Coordinate) => void }) {
  const latest = useRef({ controller, options });
  latest.current = { controller, options };
  const stopDrag = useRef<() => void>();
  useEffect(() => { if (!controller.activeTool) stopDrag.current?.(); }, [controller.activeTool]);
  useEffect(() => {
    const map = ref.current?.getMap();
    if (!map || !controller.activeTool) return;
    const enabled = map.doubleClickZoom.isEnabled();
    map.doubleClickZoom.disable();
    return () => { if (enabled) map.doubleClickZoom.enable(); };
  }, [ref, controller.activeTool]);
  useEffect(() => {
    const map = ref.current?.getMap();
    if (!map) return;
    let dragging: 'plant' | 'lasso' | null = null;
    let path: Coordinate[] = [];
    let panEnabled = false;
    let suppressClick = false;
    let clickTimer: ReturnType<typeof setTimeout> | undefined;
    const plantAt = (event: MapMouseEvent) => map.getLayer('selection-network-points')
      ? map.queryRenderedFeatures(event.point, { layers: ['selection-network-points'] })
        .find((f) => f.properties?.kind === 'plant') : undefined;
    const stop = () => {
      dragging = null;
      suppressClick = false;
      if (panEnabled) map.dragPan.enable();
      panEnabled = false;
    };
    stopDrag.current = stop;
    const down = (event: MapMouseEvent) => {
      const { controller: n, options: o } = latest.current;
      if (event.originalEvent.button !== 0 || o.blocked || n.loading) return;
      if (n.activeTool === 'erase-pipe-lasso') {
        event.preventDefault();
        panEnabled = map.dragPan.isEnabled(); map.dragPan.disable();
        dragging = 'lasso'; suppressClick = true;
        path = [[event.lngLat.lng, event.lngLat.lat]]; n.pickLasso(path, false);
        return;
      }
      if (n.activeTool) return;
      const hit = plantAt(event);
      if (!hit || !n.beginMove(String(hit.properties?.id))) return;
      event.preventDefault();
      panEnabled = map.dragPan.isEnabled(); map.dragPan.disable();
      dragging = 'plant'; suppressClick = true; o.onDragStart();
    };
    const move = (event: MapMouseEvent) => {
      if (dragging === 'lasso') {
        path = [...path, [event.lngLat.lng, event.lngLat.lat]];
        latest.current.controller.pickLasso(path, false);
      } else if (dragging) latest.current.controller.pickPoint([event.lngLat.lng, event.lngLat.lat]);
    };
    const up = (event: MapMouseEvent) => {
      if (!dragging) return;
      if (dragging === 'lasso') {
        path = [...path, [event.lngLat.lng, event.lngLat.lat]];
        latest.current.controller.pickLasso(path, true);
      } else latest.current.controller.pickPoint([event.lngLat.lng, event.lngLat.lat]);
      stop();
      suppressClick = true;
      clickTimer = setTimeout(() => { suppressClick = false; }, 0);
    };
    const leave = () => {
      if (dragging) { stop(); latest.current.controller.cancel(); }
    };
    const click = (event: MapMouseEvent) => {
      if (suppressClick) { suppressClick = false; return; }
      const { activeTool, loading, pickPoint, pickPlant } = latest.current.controller;
      if (!activeTool) {
        // UMMap's onFeatureClick has no position; the pop-in is anchored on the clicked point.
        if (loading || !map.getLayer('selection-network-lines')) return;
        const hit = map.queryRenderedFeatures(event.point, { layers: ['selection-network-lines'] })[0];
        if (hit) latest.current.options.onPipeClick?.(String(hit.properties?.id), [event.lngLat.lng, event.lngLat.lat]);
        return;
      }
      if (activeTool === 'move-plant' || activeTool === 'erase-pipe-lasso') return;
      if (activeTool === 'remove-plant') {
        const hit = plantAt(event);
        if (hit) pickPlant(String(hit.properties?.id));
      } else pickPoint([event.lngLat.lng, event.lngLat.lat]);
    };
    map.on('mousedown', down); map.on('mousemove', move); map.on('mouseup', up); map.on('mouseout', leave);
    map.on('click', click);
    return () => {
      stop(); clearTimeout(clickTimer); stopDrag.current = undefined;
      map.off('mousedown', down); map.off('mousemove', move); map.off('mouseup', up); map.off('mouseout', leave);
      map.off('click', click);
    };
  }, [ref]);
}

export function networkSource(controller: Controller, pickedPipeId?: string | null): GeoJSON.FeatureCollection {
  const { network, draft, removed, activeTool, selectedPlantId, stale } = controller;
  const { display, result, legend } = controller.density;
  // Stale results keep their colours; pipes drawn since then have no entry and turn red.
  const features: GeoJSON.Feature[] = network.pipes.map((p) => ({
    type: 'Feature', properties: { id: p.id, kind: p.kind, removed: removed.includes(p.id),
      stale: stale && p.kind === 'simulated', picked: p.id === pickedPipeId,
      densityColor: display === 'density' && result ? pipeDensityColor(result.byPipe[p.id], legend) : null },
    geometry: { type: 'LineString', coordinates: p.coordinates },
  }));
  for (const plant of network.plants) {
    const selected = selectedPlantId === plant.id;
    features.push({ type: 'Feature', properties: { id: plant.id, kind: 'plant', selected,
      removed: activeTool === 'remove-plant' && selected },
      geometry: { type: 'Point', coordinates: activeTool === 'move-plant' && selected && draft.length ? draft[0] : plant.coordinates } });
  }
  if (activeTool === 'plant' && draft.length) features.push({
    type: 'Feature', properties: { id: 'draft-plant', kind: 'plant', selected: true },
    geometry: { type: 'Point', coordinates: draft[0] },
  });
  if (activeTool === 'draw-pipe') {
    draft.forEach((p, i) => features.push({ type: 'Feature', properties: { id: `vertex-${i}`, kind: 'vertex' },
      geometry: { type: 'Point', coordinates: p } }));
    if (draft.length >= 2) features.push({ type: 'Feature', properties: { id: 'draft', kind: 'draft' },
      geometry: { type: 'LineString', coordinates: draft } });
  }
  if (activeTool === 'erase-pipe-lasso' && draft.length >= 2) features.push({
    type: 'Feature', properties: { id: 'lasso', kind: 'lasso' },
    geometry: draft.length >= 3 ? { type: 'Polygon', coordinates: [[...draft, draft[0]]] }
      : { type: 'LineString', coordinates: draft },
  });
  return { type: 'FeatureCollection', features: controller.pipesVisible ? features
    : features.filter(f => f.properties?.kind === 'plant' || f.properties?.kind === 'lasso') };
}

export const NETWORK_LAYERS: LayerSpecification[] = [
  { id: 'selection-network-lasso-fill', source: 'selection-network', type: 'fill',
    filter: ['all', ['==', ['get', 'kind'], 'lasso'], ['==', ['geometry-type'], 'Polygon']],
    paint: { 'fill-color': UMR.map.picked, 'fill-opacity': 0.12 } },
  { id: 'selection-network-lasso-outline', source: 'selection-network', type: 'line',
    filter: ['==', ['get', 'kind'], 'lasso'],
    paint: { 'line-color': UMR.map.picked, 'line-width': 2, 'line-dasharray': [2, 1] } },
  { id: 'selection-network-picked', source: 'selection-network', type: 'line',
    filter: ['==', ['get', 'picked'], true],
    paint: { 'line-width': 11, 'line-color': UMR.map.picked } },
  { id: 'selection-network-lines', source: 'selection-network', type: 'line',
    filter: ['all', ['==', ['geometry-type'], 'LineString'], ['!=', ['get', 'kind'], 'lasso']],
    paint: { 'line-width': 5, 'line-color': ['case', ['==', ['get', 'removed'], true], UMR.map.consumption[0],
      ['==', ['get', 'kind'], 'draft'], UMR.map.picked,
      ['!=', ['get', 'densityColor'], null], ['get', 'densityColor'], PIPE_COLOR],
    'line-opacity': ['case', ['any', ['==', ['get', 'removed'], true], ['==', ['get', 'stale'], true]], 0.35, 1] } },
  { id: 'selection-network-simulation', source: 'selection-network', type: 'line',
    filter: ['==', ['get', 'kind'], 'simulated'],
    paint: { 'line-width': 3, 'line-color': '#ffffff', 'line-dasharray': [2, 2],
      'line-opacity': ['case', ['==', ['get', 'stale'], true], 0.25, 0.8] } },
  { id: 'selection-network-points', source: 'selection-network', type: 'circle',
    filter: ['==', ['geometry-type'], 'Point'],
    paint: { 'circle-radius': ['case', ['==', ['get', 'kind'], 'plant'], 10, 5],
      'circle-color': UMR.map.picked, 'circle-stroke-width': ['case', ['==', ['get', 'selected'], true], 5, 3],
      'circle-opacity': ['case', ['==', ['get', 'removed'], true], 0.35, 1],
      'circle-stroke-color': ['case', ['==', ['get', 'selected'], true], UMR.map.pipe, '#ffffff'] } },
];
